/* eslint-disable @typescript-eslint/no-explicit-any */
import { AlertCircle, PencilRuler, Trash2 } from "lucide-react";
import React, { useEffect } from "react";

import { useDeleteQuestion } from "@/api/chat-settings/delete-predefined-question";
import { useGetQuestions } from "@/api/chat-settings/get-predefined-questions";
import PredefinedQuestionForm from "@/components/predefined-questions/predefined-question-form";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import NoDataFound from "@/components/layout/no-data-found";
import PaginationCombo from "@/components/ui/pagination-combo";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import useUpdateQueryParams from "@/hooks/use-update-query-params";
import { useSearchParams } from "react-router";
import useGlobalPermission from "@/store/user-permissions";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

const PredefinedQuestions = () => {
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [selectedQuestion, setSelectedQuestion] = React.useState<any>(null);

    const [searchParams] = useSearchParams();
    const { mutate: deleteQuestion, isSuccess } = useDeleteQuestion();
    const { data: questions, isLoading } = useGetQuestions({
        limit: Number(searchParams.get("per_page") || 10),
        skip: (Number(searchParams.get("page") || 1) - 1) * Number(searchParams.get("per_page") || 10),
    });

    const updateQueryParams = useUpdateQueryParams();

    const handleEdit = (id: string) => {
        updateQueryParams({ edit_question_id: id });
    };

    const handleDeleteQuestion = () => {
        if (selectedQuestion) {
            deleteQuestion({ id: selectedQuestion.id });
        }
    };

    const handleCancel = () => {
        setDialogOpen(false);
        setSelectedQuestion(null);
    };

    useEffect(() => {
        if (isSuccess) {
            setDialogOpen(false);
            setSelectedQuestion(null);
        }
    }, [isSuccess]);

    const { permissionList } = useGlobalPermission();
    const faqPermission = permissionList.find((item) => item.module.name === "Questions");

    return (
        <div className="relative">
            {!faqPermission?.can_create && (
                <Alert variant="destructive" className="absolute z-10 top-1/2 right-1/2 translate-x-1/2 -translate-y-1/2 max-w-2xl">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>You do not have permission to view this list!</AlertDescription>
                </Alert>
            )}
            <Card className={cn("p-4", !faqPermission?.can_create && "blur-md")}>
                <div className="flex justify-end py-4">
                    <PredefinedQuestionForm question={selectedQuestion} />
                </div>
                <div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[100px]">#SL</TableHead>
                                <TableHead>Question</TableHead>
                                <TableHead>Answer</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {questions?.data &&
                                questions?.data?.length > 0 &&
                                questions?.data?.map((question: any, i: number) => (
                                    <TableRow key={i}>
                                        <TableCell className="font-medium">{i + 1}</TableCell>
                                        <TableCell>{!faqPermission?.can_get_list ? "No permission to view" : question?.question}</TableCell>
                                        <TableCell>{!faqPermission?.can_get_list ? "No permission to view" : question?.answer}</TableCell>
                                        <TableCell>
                                            <Switch disabled={!faqPermission?.can_get_list} checked={question?.is_active} />
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button
                                                    disabled={!faqPermission?.can_get_list}
                                                    size={"sm"}
                                                    className="size-8 bg-orange-500 hover:bg-orange-400"
                                                    variant={"default"}
                                                    onClick={() => handleEdit(question?.id)}
                                                >
                                                    <PencilRuler size={16} />
                                                </Button>
                                                <Button
                                                    disabled={!faqPermission?.can_get_list}
                                                    onClick={() => {
                                                        setSelectedQuestion(question);
                                                        setDialogOpen(true);
                                                    }}
                                                    size={"sm"}
                                                    className="size-8"
                                                    variant={"destructive"}
                                                >
                                                    <Trash2 size={16} />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            {questions?.data && questions?.data?.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center">
                                        <NoDataFound message="No predefined questions found" />
                                    </TableCell>
                                </TableRow>
                            )}
                            {!questions?.data && !isLoading && (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center">
                                        <NoDataFound message="No predefined questions found" />
                                    </TableCell>
                                </TableRow>
                            )}
                            {isLoading && (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center">
                                        Fetching Data
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    {questions?.data && questions?.pagination?.total_entity > 0 && <PaginationCombo totalItems={questions?.pagination?.total_entity} />}
                </div>
                <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <AlertDialogTrigger asChild></AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This action cannot be undone. This will permanently delete the question and remove it from our servers.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel onClick={handleCancel}>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleDeleteQuestion}>Continue</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </Card>
        </div>
    );
};

export default PredefinedQuestions;
